import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { AppHeader } from "../components/AppHeader";
import { PersonForm } from "../components/PersonForm";
import { AddRelativeForm, type RelativeKind } from "../components/AddRelativeForm";
import { RelationshipToMeLine } from "../components/RelationshipToMeLine";
import { apiFetch } from "../lib/api";
import { useMe } from "../hooks/useMe";
import { resolveMembership } from "../hooks/useFamilyMembership";
import { can, personDisplayName, showsInTree } from "@family/shared";
import type {
  Person,
  PersonMediaListResponse,
  PersonsListResponse,
  Relationship,
  RelationshipsListResponse,
} from "@family/shared";

import { STANDING_HINT, STANDING_LABEL } from "./TreeAuditPage";

const fullName = personDisplayName;

function birthYear(p: Person): number | null {
  if (!p.birthDate) return null;
  const y = parseInt(p.birthDate.slice(0, 4), 10);
  return Number.isNaN(y) ? null : y;
}

function formatDate(d: string | null): string | null {
  if (!d) return null;
  if (d.length === 4) return d; // year only
  try {
    return new Date(d).toLocaleDateString("en-GB", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return d;
  }
}

function lifespan(p: Person): string | null {
  const b = birthYear(p);
  const d = p.deathDate ? parseInt(p.deathDate.slice(0, 4), 10) : null;
  if (!b && !d) return null;
  return `${b ?? "?"} – ${(d && !Number.isNaN(d)) ? d : ""}`;
}

interface RelatedGroup {
  label: string;
  persons: Person[];
}

function relatedGroups(
  personId: string,
  persons: Person[],
  relationships: Relationship[],
): RelatedGroup[] {
  const byId = new Map(persons.map((p) => [p.id, p]));
  const parents: Person[] = [];
  const children: Person[] = [];
  const spouses: Person[] = [];
  const siblings: Person[] = [];

  // Sibling ids collected as a set: the same person can arrive twice, once
  // per shared parent, and once more from an explicit sibling edge.
  const siblingIds = new Set<string>();
  // parent id -> their children, so a sibling is anyone who appears under a
  // parent of this person.
  const childrenOfParent = new Map<string, string[]>();
  const parentIds = new Set<string>();

  for (const r of relationships) {
    if (r.kind === "parent") {
      const list = childrenOfParent.get(r.toPerson);
      if (list) list.push(r.fromPerson);
      else childrenOfParent.set(r.toPerson, [r.fromPerson]);

      if (r.fromPerson === personId) {
        parentIds.add(r.toPerson);
        const other = byId.get(r.toPerson);
        if (other) parents.push(other);
      } else if (r.toPerson === personId) {
        const other = byId.get(r.fromPerson);
        if (other) children.push(other);
      }
    } else if (r.kind === "spouse" || r.kind === "sibling") {
      const otherId =
        r.fromPerson === personId
          ? r.toPerson
          : r.toPerson === personId
            ? r.fromPerson
            : null;
      if (otherId) {
        if (r.kind === "spouse") {
          const other = byId.get(otherId);
          if (other) spouses.push(other);
        } else {
          // Explicit sibling edges are the shortcut used when parents are
          // not recorded, so they still count — they just are not the only
          // source any more.
          siblingIds.add(otherId);
        }
      }
    }
  }

  // Anyone sharing AT LEAST ONE parent. Half-siblings are siblings here and
  // are not marked as anything else: listing only the full ones made the
  // rest conspicuous by their absence, which is the opposite of kind in a
  // family where a parent had children with more than one partner. Whoever
  // wants the detail can read the parents on each person's page.
  for (const parentId of parentIds) {
    for (const childId of childrenOfParent.get(parentId) ?? []) {
      if (childId !== personId) siblingIds.add(childId);
    }
  }
  for (const id of siblingIds) {
    const other = byId.get(id);
    if (other) siblings.push(other);
  }

  // Sort children and siblings oldest → youngest by birth year.
  const byAge = (a: Person, b: Person): number => {
    const ya = birthYear(a);
    const yb = birthYear(b);
    if (ya == null && yb == null) return 0;
    if (ya == null) return 1;
    if (yb == null) return -1;
    return ya - yb;
  };
  children.sort(byAge);
  siblings.sort(byAge);

  return [
    { label: "Parents", persons: parents },
    { label: "Spouses", persons: spouses },
    { label: "Children", persons: children },
    { label: "Siblings", persons: siblings },
  ].filter((g) => g.persons.length > 0);
}

interface RelationInvolving {
  rel: Relationship;
  other: Person;
  label: string;
}

function relationsInvolving(
  personId: string,
  persons: Person[],
  relationships: Relationship[],
): RelationInvolving[] {
  const byId = new Map(persons.map((p) => [p.id, p]));
  const out: RelationInvolving[] = [];
  for (const r of relationships) {
    let otherId: string | null = null;
    let label = "";
    if (r.kind === "parent") {
      if (r.fromPerson === personId) {
        otherId = r.toPerson;
        label = "Parent";
      } else if (r.toPerson === personId) {
        otherId = r.fromPerson;
        label = "Child";
      }
    } else if (r.kind === "spouse") {
      if (r.fromPerson === personId) otherId = r.toPerson;
      else if (r.toPerson === personId) otherId = r.fromPerson;
      label = "Spouse";
    } else if (r.kind === "sibling") {
      if (r.fromPerson === personId) otherId = r.toPerson;
      else if (r.toPerson === personId) otherId = r.fromPerson;
      label = "Sibling";
    }
    if (otherId) {
      const other = byId.get(otherId);
      if (other) out.push({ rel: r, other, label });
    }
  }
  return out;
}

function PersonAvatar({ person, size = 36 }: { person: Person; size?: number }) {
  const initials =
    (person.givenName?.[0] ?? "") + (person.familyName?.[0] ?? "");
  return (
    <div
      style={{
        width: size,
        height: size,
        flexShrink: 0,
        borderRadius: "50%",
        overflow: "hidden",
        background: person.coverUrl
          ? "var(--c-bg-alt)"
          : "linear-gradient(135deg, var(--c-accent), var(--c-accent-hover))",
        color: "var(--c-accent-contrast)",
        fontWeight: 700,
        fontSize: size * 0.35,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {person.coverUrl ? (
        <img
          src={person.coverUrl}
          alt=""
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            display: "block",
            pointerEvents: "none",
          }}
          draggable={false}
          onContextMenu={(e) => e.preventDefault()}
        />
      ) : (
        <span>{initials}</span>
      )}
    </div>
  );
}

function RelatedPersonItem({ person }: { person: Person }) {
  return (
    <Link
      to={`/person/${person.id}`}
      style={{
        display: "flex",
        alignItems: "center",
        gap: "var(--s-2)",
        textDecoration: "none",
        color: "var(--c-text)",
        padding: "6px 0",
      }}
    >
      <PersonAvatar person={person} size={32} />
      <div style={{ minWidth: 0 }}>
        <div style={{ fontWeight: 500, fontSize: "0.9rem", lineHeight: 1.3 }}>
          {fullName(person)}
        </div>
        {lifespan(person) && (
          <div style={{ fontSize: "0.8rem", color: "var(--c-text-subtle)" }}>
            {lifespan(person)}
          </div>
        )}
      </div>
    </Link>
  );
}

export function PersonPage() {
  const { personId } = useParams<{ personId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(searchParams.get("edit") === "1");
  const [addingRelative, setAddingRelative] = useState<RelativeKind | null>(null);

  useEffect(() => {
    if (!editing && searchParams.get("edit") === "1") {
      searchParams.delete("edit");
      setSearchParams(searchParams, { replace: true });
    }
  }, [editing, searchParams, setSearchParams]);

  const meQuery = useMe();
  const primary = resolveMembership(meQuery.data);
  const slug = primary?.familySlug;

  const personQuery = useQuery<Person>({
    queryKey: ["person", slug, personId],
    queryFn: () =>
      apiFetch<Person>(`/families/${slug}/persons/${personId}`),
    enabled: Boolean(slug && personId),
  });

  const personsQuery = useQuery<PersonsListResponse>({
    queryKey: ["persons", slug],
    queryFn: () => apiFetch<PersonsListResponse>(`/families/${slug}/persons`),
    enabled: Boolean(slug),
  });

  const relsQuery = useQuery<RelationshipsListResponse>({
    queryKey: ["relationships", slug],
    queryFn: () =>
      apiFetch<RelationshipsListResponse>(
        `/families/${slug}/relationships`,
      ),
    enabled: Boolean(slug),
  });

  const personMediaQuery = useQuery<PersonMediaListResponse>({
    queryKey: ["person-media", slug, personId],
    queryFn: () =>
      apiFetch<PersonMediaListResponse>(
        `/families/${slug}/persons/${personId}/media`,
      ),
    enabled: Boolean(slug && personId),
  });

  const deletePerson = useMutation<void, Error, void>({
    mutationFn: () =>
      apiFetch(`/families/${slug}/persons/${personId}`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["persons", slug] });
      void queryClient.invalidateQueries({
        queryKey: ["relationships", slug],
      });
      navigate("/tree", { replace: true });
    },
  });

  const removeRelationship = useMutation<void, Error, string>({
    mutationFn: (relId) =>
      apiFetch(`/families/${slug}/relationships/${relId}`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["relationships", slug],
      });
    },
  });

  if (!primary || !slug) {
    return (
      <>
        <AppHeader />
        <main className="page">
          <p className="text-muted">Loading…</p>
        </main>
      </>
    );
  }

  const canEdit = can(primary.role, "tree:edit");
  // Self-edit carve-out: anyone may edit the person linked to themselves
  // (personal fields only — the API enforces the same rule). Relationship
  // editing and Delete stay behind tree:edit.
  const isSelf =
    Boolean(personId) && personId === meQuery.data?.user.linkedPersonId;
  const canEditPerson = canEdit || isSelf;
  const groups =
    personQuery.data && personsQuery.data && relsQuery.data
      ? relatedGroups(
          personQuery.data.id,
          personsQuery.data.persons,
          relsQuery.data.relationships,
        )
      : [];

  const personRelations =
    personQuery.data && personsQuery.data && relsQuery.data
      ? relationsInvolving(
          personQuery.data.id,
          personsQuery.data.persons,
          relsQuery.data.relationships,
        )
      : [];

  function handleDelete() {
    if (!personQuery.data) return;
    const ok = window.confirm(
      `Delete ${fullName(personQuery.data)}? This also removes their relationships. Cannot be undone.`,
    );
    if (ok) deletePerson.mutate();
  }

  const person = personQuery.data;
  const parentGroup = groups.find((g) => g.label === "Parents");
  const childrenGroup = groups.find((g) => g.label === "Children");
  const siblingsGroup = groups.find((g) => g.label === "Siblings");
  const spousesGroup = groups.find((g) => g.label === "Spouses");

  return (
    <>
      <AppHeader />
      <main className="page stack-lg">
        <Link to="/tree" className="text-muted" style={{ textDecoration: "none" }}>
          ← Back to tree
        </Link>

        {personQuery.isLoading && <p className="text-muted">Loading…</p>}
        {personQuery.error && (
          <p className="text-error">
            {(personQuery.error as Error).message.includes("404")
              ? "Person not found."
              : (personQuery.error as Error).message}
          </p>
        )}

        {person && editing && (
          <PersonForm
            familySlug={slug}
            person={person}
            onSaved={() => setEditing(false)}
            onCancel={() => setEditing(false)}
          />
        )}

        {person && !editing && (
          <>
            {/* Two-column editorial layout */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 320px",
                gap: "var(--s-6, 2rem)",
                alignItems: "start",
              }}
              className="person-profile-grid"
            >
              {/* LEFT column: Name, lifespan, cover photo, bio */}
              <div style={{ display: "flex", flexDirection: "column", gap: "var(--s-4, 1.5rem)" }}>
                <div>
                  <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "var(--s-3)" }}>
                    <h1
                      style={{
                        fontFamily: "var(--font-heading)",
                        fontSize: "2.5rem",
                        lineHeight: 1.1,
                        margin: 0,
                        color: "var(--c-text)",
                      }}
                    >
                      {fullName(person)}
                    </h1>
                    {person.standing && !showsInTree(person.standing) && (
                      <p
                        className="text-muted"
                        style={{ margin: "6px 0 0", fontSize: "0.95rem" }}
                      >
                        <strong>{STANDING_LABEL[person.standing]}.</strong>{" "}
                        {STANDING_HINT[person.standing]} Not shown in the family
                        tree — this page is theirs, and their relatives are
                        listed below.
                      </p>
                    )}
                    {canEditPerson && (
                      <div style={{ display: "flex", gap: "var(--s-2)", flexShrink: 0, marginTop: 8 }}>
                        <button
                          className="btn-tertiary"
                          onClick={() => setEditing(true)}
                        >
                          Edit
                        </button>
                        {canEdit && (
                          <button
                            className="btn-tertiary"
                            onClick={handleDelete}
                            disabled={deletePerson.isPending}
                            style={{ color: "var(--c-danger)" }}
                          >
                            {deletePerson.isPending ? "Deleting…" : "Delete"}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                  {lifespan(person) && (
                    <p
                      style={{
                        fontSize: "1.1rem",
                        margin: "6px 0 0",
                        color: "var(--c-text-subtle)",
                      }}
                    >
                      {lifespan(person)}
                    </p>
                  )}
                  <RelationshipToMeLine
                    familySlug={slug}
                    personId={person.id}
                  />
                </div>

                {/* Cover photo */}
                {person.coverUrl && (
                  <div
                    style={{
                      borderRadius: 8,
                      overflow: "hidden",
                      background: "var(--c-bg-alt)",
                      maxHeight: 420,
                      display: "flex",
                      justifyContent: "center",
                    }}
                  >
                    {/* Natural aspect ratio, capped at 420px tall — width:100%
                        + objectFit:cover turned portraits into a 3:1 crop on
                        wide screens, beheading the subject. */}
                    <img
                      src={person.coverUrl}
                      alt={fullName(person)}
                      style={{
                        maxWidth: "100%",
                        maxHeight: 420,
                        width: "auto",
                        height: "auto",
                        display: "block",
                        pointerEvents: "none",
                      }}
                      draggable={false}
                      onContextMenu={(e) => e.preventDefault()}
                    />
                  </div>
                )}

                {/* Bio */}
                {person.bio && (
                  <p style={{ whiteSpace: "pre-wrap", lineHeight: 1.7, margin: 0, color: "var(--c-text)" }}>
                    {person.bio}
                  </p>
                )}

                {deletePerson.error && (
                  <p className="text-error">{deletePerson.error.message}</p>
                )}
              </div>

              {/* RIGHT sidebar: Vital details + relationships */}
              <aside
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "var(--s-4, 1.5rem)",
                  padding: "var(--s-4, 1.5rem)",
                  background: "var(--c-bg-alt, #f8f8f8)",
                  borderRadius: 8,
                  border: "1px solid var(--c-border, #e0e0e0)",
                }}
              >
                {/* Born */}
                {person.birthDate && (
                  <div>
                    <div style={{ fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--c-text-subtle)", marginBottom: 4 }}>
                      Born
                    </div>
                    <div style={{ fontSize: "0.95rem", color: "var(--c-text)" }}>
                      {formatDate(person.birthDate)}
                    </div>
                  </div>
                )}

                {/* Died */}
                {person.deathDate && (
                  <div>
                    <div style={{ fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--c-text-subtle)", marginBottom: 4 }}>
                      Died
                    </div>
                    <div style={{ fontSize: "0.95rem", color: "var(--c-text)" }}>
                      {formatDate(person.deathDate)}
                    </div>
                  </div>
                )}

                {/* Gender */}
                {person.gender && (
                  <div>
                    <div style={{ fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--c-text-subtle)", marginBottom: 4 }}>
                      Gender
                    </div>
                    <div style={{ fontSize: "0.95rem", color: "var(--c-text)", textTransform: "capitalize" }}>
                      {person.gender}
                    </div>
                  </div>
                )}

                {/* Parents / Spouses / Children — editors also see the
                    section when it's empty, so they can add from here. */}
                {([
                  ["Parents", "parent", parentGroup] as const,
                  ["Spouses", "spouse", spousesGroup] as const,
                  ["Children", "child", childrenGroup] as const,
                ]).map(([label, relation, group]) =>
                  group || canEdit ? (
                    <div key={relation}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          marginBottom: 8,
                        }}
                      >
                        <div style={{ fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--c-text-subtle)" }}>
                          {label}
                        </div>
                        {canEdit && (
                          <button
                            type="button"
                            className="btn-tertiary"
                            onClick={() => setAddingRelative(relation)}
                            style={{ fontSize: "0.8rem", minHeight: 24, padding: "0 6px" }}
                          >
                            + Add
                          </button>
                        )}
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                        {(group?.persons ?? []).map((p) => (
                          <RelatedPersonItem key={p.id} person={p} />
                        ))}
                        {!group && (
                          <span className="text-subtle" style={{ fontSize: "0.85rem" }}>
                            None yet
                          </span>
                        )}
                      </div>
                    </div>
                  ) : null,
                )}

                {/* Siblings */}
                {siblingsGroup && (
                  <div>
                    <div style={{ fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--c-text-subtle)", marginBottom: 8 }}>
                      Siblings
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                      {siblingsGroup.persons.map((p) => (
                        <RelatedPersonItem key={p.id} person={p} />
                      ))}
                    </div>
                  </div>
                )}
              </aside>
            </div>

            {/* Add parent/spouse/child panel (editor-only) */}
            {addingRelative && personsQuery.data && (
              <AddRelativeForm
                familySlug={slug}
                person={person}
                relation={addingRelative}
                persons={personsQuery.data.persons}
                excludeIds={(addingRelative === "parent"
                  ? parentGroup
                  : addingRelative === "spouse"
                    ? spousesGroup
                    : childrenGroup
                )?.persons.map((p) => p.id) ?? []}
                onDone={() => setAddingRelative(null)}
                onCancel={() => setAddingRelative(null)}
              />
            )}

            {/* Relationship management (editor-only) */}
            {canEdit && personRelations.length > 0 && (
              <section className="stack">
                <h2>Relationships</h2>
                <ul className="list-unstyled">
                  {personRelations.map((r) => (
                    <li key={r.rel.id} className="card">
                      <div className="row-between">
                        <span>
                          <span className="badge">{r.label}</span>{" "}
                          <Link
                            to={`/person/${r.other.id}`}
                            style={{
                              textDecoration: "none",
                              color: "var(--c-text)",
                              marginLeft: "var(--s-2)",
                            }}
                          >
                            {fullName(r.other)}
                          </Link>
                        </span>
                        <button
                          className="btn-tertiary"
                          onClick={() => {
                            if (window.confirm("Remove this relationship?")) {
                              removeRelationship.mutate(r.rel.id);
                            }
                          }}
                          disabled={removeRelationship.isPending}
                          style={{ color: "var(--c-danger)" }}
                        >
                          Remove
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
                {removeRelationship.error && (
                  <p className="text-error">{removeRelationship.error.message}</p>
                )}
              </section>
            )}

            {/* Appears in — full width photo grid */}
            {personMediaQuery.data && personMediaQuery.data.media.length > 0 && (
              <section className="stack">
                <h2>Appears in</h2>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns:
                      "repeat(auto-fill, minmax(160px, 1fr))",
                    gap: "var(--s-2)",
                  }}
                >
                  {personMediaQuery.data.media.map((item) => (
                    <Link
                      key={item.mediaId}
                      to={`/post/${item.postId}`}
                      title={item.postTitle ?? "Open post"}
                      style={{
                        display: "block",
                        aspectRatio: "1 / 1",
                        overflow: "hidden",
                        borderRadius: 4,
                        background: "var(--c-bg-alt)",
                        position: "relative",
                      }}
                    >
                      <img
                        // Thumbnail for the grid cell; the link through to the
                        // post is where the original gets loaded.
                        src={item.thumbnailUrl ?? item.viewUrl}
                        alt=""
                        loading="lazy"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          display: "block",
                          pointerEvents: "none",
                        }}
                        onContextMenu={(e) => e.preventDefault()}
                        draggable={false}
                      />
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </>
        )}
      </main>

      {/* Responsive: stack columns on mobile */}
      <style>{`
        @media (max-width: 768px) {
          .person-profile-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </>
  );
}
